import {observable} from 'mobx';
import uniqid from 'uniqid';
import GeneralSelectorTableStore, {NoneKPIColumns, SelectorTableColumnDef} from '../GeneralSelectorTableStore';
import {SelectorTableLayout} from '../SelectorTableLayout';
import * as InsightsAPI from '../../apis/InsightsAPI';
import {numberFormat, percentFormat, textFormat} from '../../utility/FormatValueUtils';
import Store, {AnalyticsTab} from '../store';
import {DescriptiveKPIAdditions, KPI, KPIGroupKey, ProductKPI, ProductSlopeOutlierKPI, SessionInsightKPI} from '../../kpi';
import {TableDataForChannel} from '../../types/api/tableData/TableDataForChannel';
import {MessageProvider} from '../../i18n/i18n';
import {Channel} from '../../types/Channel';
import {ChannelState} from '../ChannelStore';
import {TimeState} from '../TimeIntervalStore';
import {DateUtils} from '../../utility/DateUtils';
import {TableSort, TableSortDirection} from '../../types/Table';
import {SearchtermFromProductIdResponse} from '../../types/api/insights/SearchtermFromProductId';
import {DataForPrimaryAndSecondaryAttribute} from '../InsightsTableStore';

class ProductSelectorTableStore extends GeneralSelectorTableStore {
    constructor(mainStore: Store, t: MessageProvider, protected readonly channelState: ChannelState, timeState: TimeState) {
        super(mainStore, t, channelState, timeState);
        this.updateTableSettingsFromLocalStorage('product');
    }

    protected dependantAnalyticsTab: AnalyticsTab = AnalyticsTab.ProductView;

    public readonly defaultSorting: TableSort = {
        sortDirection: TableSortDirection.DESC,
        attribute: ProductKPI.Sales,
    };

    public tableSorting: TableSort = {...this.defaultSorting};

    public get tableColumnList(): SelectorTableColumnDef[] {
        return this.resizeColumns(
            this.withI18N([
                {
                    dataKey: NoneKPIColumns.RowNumber,
                    minWidth: 50,
                    align: 'right',
                    isSortable: false,
                    valueFormat: textFormat(),
                    channelComparisonEnabled: false,
                },
                {
                    dataKey: KPIGroupKey.ProductId,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: textFormat(),
                    channelComparisonEnabled: false,
                },
                {
                    dataKey: DescriptiveKPIAdditions.Title,
                    minWidth: 400,
                    align: 'left',
                    valueFormat: textFormat(),
                    withRevealOnHover: true,
                    channelComparisonEnabled: false,
                },
                {
                    dataKey: ProductKPI.ClickCount,
                    sessionApiKey: ProductKPI.ClickCount,
                    minWidth: 120,
                    align: 'right',
                    scoreAttributes: {
                        slope: ProductSlopeOutlierKPI.ClickSlopeValue,
                        outlier: ProductSlopeOutlierKPI.ClickOutlierScore,
                        maxSlope: ProductSlopeOutlierKPI.MaxClickSlopeValue,
                        minSlope: ProductSlopeOutlierKPI.MinClickSlopeValue,
                    },
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.AsnClickCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.NaviClickCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.UniqueClickCount,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.CartCount,
                    sessionApiKey: SessionInsightKPI.CartTotalAmount,
                    minWidth: 120,
                    align: 'right',
                    scoreAttributes: {
                        slope: ProductSlopeOutlierKPI.CartSlopeValue,
                        outlier: ProductSlopeOutlierKPI.CartOutlierScore,
                        maxSlope: ProductSlopeOutlierKPI.MaxCartSlopeValue,
                        minSlope: ProductSlopeOutlierKPI.MinCartSlopeValue,
                    },
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.ClickToCartRate,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: percentFormat(),
                    isRate: true,
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.CheckoutCount,
                    sessionApiKey: SessionInsightKPI.CheckoutTotalAmount,
                    minWidth: 120,
                    align: 'right',
                    scoreAttributes: {
                        slope: ProductSlopeOutlierKPI.CheckoutSlopeValue,
                        outlier: ProductSlopeOutlierKPI.CheckoutOutlierScore,
                        maxSlope: ProductSlopeOutlierKPI.MaxCheckoutSlopeValue,
                        minSlope: ProductSlopeOutlierKPI.MinCheckoutSlopeValue,
                    },
                    valueFormat: numberFormat(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.CartToCheckoutRate,
                    minWidth: 120,
                    align: 'right',
                    valueFormat: percentFormat(),
                    isRate: true,
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.Sales,
                    sessionApiKey: ProductKPI.Sales,
                    minWidth: 140,
                    align: 'right',
                    scoreAttributes: {
                        slope: ProductSlopeOutlierKPI.SalesSlopeValue,
                        outlier: ProductSlopeOutlierKPI.SalesOutlierScore,
                        maxSlope: ProductSlopeOutlierKPI.MaxSalesSlopeValue,
                        minSlope: ProductSlopeOutlierKPI.MinSalesSlopeValue,
                    },
                    usesMoneyFormat: true,
                    valueFormat: this.getMoneyFormatter(),
                    isChartable: true,
                    withFilter: true,
                },
                {
                    dataKey: ProductKPI.SalesPerClick,
                    minWidth: 120,
                    align: 'right',
                    usesMoneyFormat: true,
                    valueFormat: this.getMoneyFormatter(),
                    isChartable: true,
                    withFilter: true,
                },
            ])
        );
    }

    public set tableColumnList(_: SelectorTableColumnDef[]) {
        // Dummy
    }

    public readonly layout = new SelectorTableLayout(
        this.tableColumns,
        () => this.mainStore.compareStore,
        [
            {
                name: 'fixed',
                label: '',
                columns: [NoneKPIColumns.RowNumber, KPIGroupKey.ProductId, DescriptiveKPIAdditions.Title],
            },
            {
                name: 'funnel',
                label: this.t('table:table.funnel'),
                columns: [
                    ProductKPI.ClickCount,
                    ProductKPI.AsnClickCount,
                    ProductKPI.NaviClickCount,
                    ProductKPI.UniqueClickCount,
                    ProductKPI.CartCount,
                    ProductKPI.CheckoutCount,
                ],
                hiddenColumns: [ProductKPI.AsnClickCount, ProductKPI.NaviClickCount, ProductKPI.UniqueClickCount],
            },
            {
                name: 'rates',
                label: this.t('table:table.rates'),
                columns: [ProductKPI.ClickToCartRate, ProductKPI.CartToCheckoutRate],
            },
            {
                name: 'salesKpis',
                label: this.t('table:table.salesKpis'),
                columns: [ProductKPI.Sales, ProductKPI.SalesPerClick],
            },
        ],
        this.dependantAnalyticsTab,
        this.saveTableLayoutToLocalStorage('product'),
        this.getTableLayoutFromLocalStorage('product'),
        this.recomputeMultiGridSizeIfPossible
    );

    // table cell drilldown session modal
    @observable public cellAttributeSessionModal = null;
    @observable public sessionsForPrimaryAndSecondaryAttribute = [];
    @observable public selectedSessionForPrimaryAndSecondaryAttribute = null;
    @observable public sessionDataForPrimaryAndSecondaryAttribute = [];

    public fetchInsightsData(productId: string, kpi: KPI, channel: Channel): void {
        const {insightsTableStore} = this.mainStore;

        InsightsAPI.searchtermsFromProductId({
            [KPIGroupKey.ProductId]: productId,
            channel: channel.name,
            interval: DateUtils.toHourlyDateRange(this.mainStore.timeIntervalStore.mainDateRange),
        })
            .then((resp) => {
                const convertedSearchtermsForProductId = this.convertSearchtermsForProductToFitFrontend(resp.data);
                insightsTableStore.insightsTableData = {
                    loading: false,
                    data: insightsTableStore.sortForPrimaryAndSecondaryAttribute(convertedSearchtermsForProductId),
                };
            })
            .catch(() => {
                insightsTableStore.insightsTableData = {
                    loading: false,
                    data: [],
                };
            });
    }

    private convertSearchtermsForProductToFitFrontend(
        searchtermsForProduct: SearchtermFromProductIdResponse[]
    ): DataForPrimaryAndSecondaryAttribute[] {
        return searchtermsForProduct.map((singleSearchterm) => {
            return {
                id: uniqid(),
                [SessionInsightKPI.SearchEvents]: singleSearchterm.kpis[SessionInsightKPI.SearchEvents],
                [SessionInsightKPI.CartEvents]: singleSearchterm.kpis[SessionInsightKPI.CartEvents],
                [SessionInsightKPI.CartTotalAmount]: singleSearchterm.kpis[SessionInsightKPI.CartTotalAmount],
                [SessionInsightKPI.CheckoutEvents]: singleSearchterm.kpis[SessionInsightKPI.CheckoutEvents],
                [SessionInsightKPI.CheckoutTotalAmount]: singleSearchterm.kpis[SessionInsightKPI.CheckoutTotalAmount],
                [ProductKPI.ClickCount]: singleSearchterm.kpis[ProductKPI.ClickCount],
                [ProductKPI.Sales]: singleSearchterm.kpis[ProductKPI.Sales],
                [KPIGroupKey.Searchterm]: singleSearchterm[KPIGroupKey.Searchterm],
            };
        });
    }

    public calculateRatesForStickySumRow(
        data: TableDataForChannel,
        channel: Channel,
        searchterm: string,
        variantCompareActive = false
    ): TableDataForChannel {
        const newData = {...data, [NoneKPIColumns.RowNumber]: ''};
        if (searchterm !== '') {
            this.channelSumRowIsChartable = false;
            newData[KPIGroupKey.ProductId] = this.t('table:all');
            newData[DescriptiveKPIAdditions.Title] = searchterm;
            return newData;
        }

        if (data.channelCompareRegister) {
            const channelNames = Object.keys(data.channelCompareRegister);
            this.channelSumRowIsChartable = true;
            const channelKey = variantCompareActive ? 'table:variant' : 'table:channel';
            newData[KPIGroupKey.ProductId] = this.t(channelKey, {count: channelNames.length});
            newData[DescriptiveKPIAdditions.Title] = channelNames.join(', ');
            return newData;
        }

        this.channelSumRowIsChartable = true;
        newData[KPIGroupKey.ProductId] = this.t('table:channel');
        newData[DescriptiveKPIAdditions.Title] = channel.name;
        return newData;
    }
}

export default ProductSelectorTableStore;



// WEBPACK FOOTER //
// ./src/stores/product/ProductSelectorTableStore.ts